import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isPaymentTab, type PaymentTab } from '@/lib/paymentTabs';

/**
 * Calls `onTab` for a `?tab=` link that lands on a payment page while it is open. The chat's
 * page button navigates in place, so the link arrives as a new search on the same mount,
 * after the page's initialiser has read the first one — which stays the initialiser's.
 *
 * `onTab` must be stable across renders.
 */
export function useTabLinkWhileOpen(onTab: (tab: PaymentTab) => void): void {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  // By value: the same search re-rendered is not a new link, whatever object carries it.
  const lastSearch = useRef(search);
  const tab = searchParams.get('tab');

  useEffect(() => {
    if (search === lastSearch.current) return;
    lastSearch.current = search;
    if (isPaymentTab(tab)) onTab(tab);
  }, [search, tab, onTab]);
}
